import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  Settings,
  Save,
  Refresh,
  Close,
  Business
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { url } from '../utils/constants';

const FeeSettingsModal = ({ open, onClose, onSettingsUpdated }) => {
  const { getToken, user } = useAuth();
  const [feeSettings, setFeeSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [empresas, setEmpresas] = useState([]);
  const [selectedEmpresaEmail, setSelectedEmpresaEmail] = useState('');
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const loadEmpresas = useCallback(async () => {
    if (!isSuperAdmin || !getToken()) return;
    try {
      const { data } = await axios.get(`${url}/api/v1/user/empresas`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setEmpresas(data?.data || []);
    } catch (err) {
      setEmpresas([]);
    }
  }, [isSuperAdmin, getToken]);

  const loadFeeSettings = useCallback(async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const token = getToken();
      if (!token) throw new Error('Token não encontrado');

      const headers = { Authorization: `Bearer ${token}` };
      const params = isSuperAdmin && selectedEmpresaEmail ? { targetEmail: selectedEmpresaEmail } : {};
      const response = await axios.get(`${url}/api/v1/fee-settings`, { headers, params });

      setFeeSettings(response.data.data);
      setMessage({ type: 'success', text: 'Configurações carregadas com sucesso!' });
    } catch (error) {
      console.error('❌ Erro ao carregar configurações:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar configurações de taxas' });
    } finally {
      setLoading(false);
    }
  }, [getToken, isSuperAdmin, selectedEmpresaEmail]);

  useEffect(() => {
    if (open) {
      if (isSuperAdmin) loadEmpresas();
    } else {
      setSelectedEmpresaEmail('');
    }
  }, [open, isSuperAdmin, loadEmpresas]);

  useEffect(() => {
    if (open) {
      if (isSuperAdmin && !selectedEmpresaEmail) {
        setFeeSettings(null);
        setLoading(false);
        return;
      }
      loadFeeSettings();
    }
  }, [open, isSuperAdmin, selectedEmpresaEmail]);

  const saveFeeSettings = async () => {
    setSaving(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const payload = { ...feeSettings };
      if (isSuperAdmin && selectedEmpresaEmail) payload.targetEmail = selectedEmpresaEmail;

      await axios.put(`${url}/api/v1/fee-settings`, payload, { headers });
      setMessage({ type: 'success', text: 'Configurações salvas com sucesso!' });

      if (onSettingsUpdated) onSettingsUpdated();
      window.dispatchEvent(new CustomEvent('feeSettingsUpdated'));
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao salvar configurações' });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    setMessage({ type: '', text: '' });
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const body = isSuperAdmin && selectedEmpresaEmail ? { targetEmail: selectedEmpresaEmail } : {};

      const response = await axios.post(`${url}/api/v1/fee-settings/reset`, body, { headers });
      setFeeSettings(response.data.data);
      setMessage({ type: 'success', text: 'Configurações resetadas para padrão' });

      if (onSettingsUpdated) onSettingsUpdated();
      window.dispatchEvent(new CustomEvent('feeSettingsUpdated'));
    } catch (error) {
      console.error('❌ Erro ao resetar configurações:', error);
      setMessage({ type: 'error', text: 'Erro ao resetar configurações' });
    }
  };

  const updateServiceFee = (value) => {
    setFeeSettings(prev => ({
      ...prev,
      serviceFee: parseFloat(value) || 0
    }));
  };

  const updateServiceFeeActive = (isActive) => {
    setFeeSettings(prev => ({
      ...prev,
      serviceFeeActive: isActive
    }));
  };

  const updateExchangeRate = (pair, field, value) => {
    setFeeSettings(prev => ({
      ...prev,
      exchangeRates: {
        ...prev.exchangeRates,
        [pair]: {
          ...prev.exchangeRates[pair],
          [field]: field === 'rate' ? parseFloat(value) || 0 : value
        }
      }
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          margin: { xs: 2, sm: 3 },
          width: { xs: '100%', sm: '90%', md: '80%' }
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Settings color="primary" />
            <Typography variant="h6">Configurações de Taxas</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        {message.text && (
          <Alert severity={message.type} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        {isSuperAdmin && (
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="fee-settings-empresa-label">Empresa</InputLabel>
              <Select
                labelId="fee-settings-empresa-label"
                label="Empresa"
                value={selectedEmpresaEmail}
                onChange={(e) => setSelectedEmpresaEmail(e.target.value)}
                startAdornment={selectedEmpresaEmail ? <Business sx={{ mr: 1, color: 'action.active' }} /> : null}
              >
                <MenuItem value="">
                  <em>Selecione uma empresa</em>
                </MenuItem>
                {empresas.map((emp) => (
                  <MenuItem key={emp._id || emp.email} value={emp.email}>
                    {emp.empresa || emp.fullname || emp.email} {emp.cnpj ? `(${emp.cnpj})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!selectedEmpresaEmail && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Selecione uma empresa para ver e alterar as taxas dela.
              </Typography>
            )}
          </Box>
        )}

        {!feeSettings && !(isSuperAdmin && !selectedEmpresaEmail) ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              Carregando configurações...
            </Typography>
          </Box>
        ) : (isSuperAdmin && !selectedEmpresaEmail) ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Selecione uma empresa acima para carregar e editar as taxas.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* Taxa de Serviço */}
            <Grid item xs={12}>
              <Card elevation={1} sx={{ mb: 2 }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: '1.1rem' }}>
                    Taxa de Serviço
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <TextField
                    fullWidth
                    label="Taxa de Serviço (%)"
                    type="number"
                    value={feeSettings?.serviceFee || 0}
                    onChange={(e) => updateServiceFee(e.target.value)}
                    InputProps={{
                      endAdornment: <Typography sx={{ ml: 1 }}>%</Typography>
                    }}
                    helperText="Taxa de serviço única aplicada sobre qualquer valor convertido"
                    size="small"
                    disabled={!feeSettings?.serviceFeeActive}
                    sx={{ mb: 2 }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={feeSettings?.serviceFeeActive || false}
                        onChange={(e) => updateServiceFeeActive(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Taxa de serviço ativa"
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Taxas de Câmbio */}
            <Grid item xs={12}>
              <Card elevation={1}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: '1.1rem' }}>
                    Taxas de Câmbio
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    {feeSettings?.exchangeRates && Object.entries(feeSettings.exchangeRates).map(([pair, rateData]) => (
                      <Grid item xs={12} sm={6} key={pair}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                          <CardContent sx={{ py: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ fontSize: '0.95rem' }}>
                              {pair === 'ARS_BRL' ? 'ARS → BRL' : 'BRL → ARS'}
                            </Typography>

                            <TextField
                              fullWidth
                              label="Taxa de Câmbio"
                              type="number"
                              value={rateData?.rate || 0}
                              onChange={(e) => updateExchangeRate(pair, 'rate', e.target.value)}
                              step="0.0001"
                              sx={{ mb: 1.5 }}
                              size="small"
                            />

                            <TextField
                              fullWidth
                              label="Descrição"
                              value={rateData?.description || ''}
                              onChange={(e) => updateExchangeRate(pair, 'description', e.target.value)}
                              sx={{ mb: 1.5 }}
                              size="small"
                            />

                            <FormControlLabel
                              control={
                                <Switch
                                  checked={rateData?.isActive || false}
                                  onChange={(e) => updateExchangeRate(pair, 'isActive', e.target.checked)}
                                  size="small"
                                />
                              }
                              label="Ativo"
                              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Button onClick={resetToDefault} startIcon={<Refresh />} color="warning">
          Resetar
        </Button>
        <Button
          onClick={saveFeeSettings}
          variant="contained"
          startIcon={<Save />}
          disabled={saving}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeeSettingsModal;
